import { useCallback, useEffect, useState } from 'react';
import { GitCompare, History } from 'lucide-react';
import { apiClient } from '../../lib/api-client';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Skeleton } from '../ui/Skeleton';
import { Badge } from '../ui/Badge';

export interface HistoryTimelineItem {
  id: string;
  at: string;
  title: string;
  description: string;
  eventType?: string;
  before?: Record<string, string | number>;
  after?: Record<string, string | number>;
}

function pickMetrics(meta: unknown): { before?: Record<string, string | number>; after?: Record<string, string | number> } {
  if (!meta || typeof meta !== 'object') return {};
  const m = meta as Record<string, unknown>;
  const before = (m.beforeMetrics ?? m.before) as Record<string, string | number> | undefined;
  const after = (m.afterMetrics ?? m.after) as Record<string, string | number> | undefined;
  const safe = (o: unknown): Record<string, string | number> | undefined => {
    if (!o || typeof o !== 'object') return undefined;
    const out: Record<string, string | number> = {};
    for (const [k, v] of Object.entries(o as Record<string, unknown>)) {
      if (typeof v === 'number' || typeof v === 'string') out[k] = v;
    }
    return Object.keys(out).length ? out : undefined;
  };
  return { before: safe(before), after: safe(after) };
}

function normalizeActivity(raw: Record<string, unknown>, index: number): HistoryTimelineItem {
  const id = String(raw.id ?? `evt-${index}`);
  const at = String(raw.timestamp ?? raw.at ?? '');
  const eventType = raw.eventType != null ? String(raw.eventType) : undefined;
  const description = String(raw.description ?? '');
  const title =
    eventType != null
      ? eventType.replace(/_/g, ' ')
      : description.slice(0, 48) + (description.length > 48 ? '…' : '');
  const { before, after } = pickMetrics(raw.metadata);
  return { id, at, title, description, eventType, before, after };
}

function formatWhen(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function MetricsTable({
  label,
  data,
}: {
  label: string;
  data: Record<string, string | number>;
}) {
  return (
    <div className="rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wide bg-gray-50 dark:bg-gray-800/80 text-gray-600 dark:text-gray-300">
        {label}
      </div>
      <dl className="divide-y divide-gray-100 dark:divide-gray-800 text-xs">
        {Object.entries(data).map(([k, v]) => (
          <div key={k} className="flex justify-between gap-2 px-2 py-1.5">
            <dt className="text-gray-500 dark:text-gray-400 truncate" title={k}>
              {k}
            </dt>
            <dd className="font-mono text-gray-900 dark:text-gray-100 shrink-0">{String(v)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export interface HistoryTabProps {
  streamId: string;
}

export function HistoryTab({ streamId }: HistoryTabProps) {
  const [items, setItems] = useState<HistoryTimelineItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!streamId) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiClient.getAiWorkflowActivity({ streamId, limit: 80 });
      const list = Array.isArray(res.entries)
        ? res.entries.map((e, i) => normalizeActivity(e as Record<string, unknown>, i))
        : [];
      setItems(list);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load history');
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [streamId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-28 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 dark:border-red-900/40">
        <CardContent className="py-6 text-sm text-red-700 dark:text-red-300">{error}</CardContent>
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-gray-500 dark:text-gray-400">
          <History className="w-10 h-10 mx-auto mb-3 opacity-60" />
          <p>No activity history for this stream yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="relative pl-4 sm:pl-6 border-l-2 border-blue-200 dark:border-blue-900 space-y-6 ml-2">
      {items.map((item) => (
        <article key={item.id} className="relative">
          <span
            className="absolute -left-[calc(0.5rem+5px)] sm:-left-[calc(0.75rem+5px)] top-1.5 w-2.5 h-2.5 rounded-full bg-blue-500 dark:bg-blue-400 ring-4 ring-white dark:ring-gray-950"
            aria-hidden
          />
          <Card className="border-gray-200 dark:border-gray-700">
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center gap-2 justify-between">
                <CardTitle className="text-base capitalize text-gray-900 dark:text-gray-100">{item.title}</CardTitle>
                <time className="text-xs text-gray-500 dark:text-gray-400" dateTime={item.at}>
                  {formatWhen(item.at)}
                </time>
              </div>
              {item.eventType && (
                <Badge variant="outline" className="w-fit text-[10px] uppercase">
                  {item.eventType}
                </Badge>
              )}
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-gray-700 dark:text-gray-300">
              <p className="leading-relaxed whitespace-pre-wrap">{item.description}</p>
              {(item.before || item.after) && (
                <div className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <GitCompare className="w-4 h-4 mt-0.5 shrink-0" />
                  <div className="grid sm:grid-cols-2 gap-3 w-full">
                    {item.before && Object.keys(item.before).length > 0 && (
                      <MetricsTable label="Before" data={item.before} />
                    )}
                    {item.after && Object.keys(item.after).length > 0 && (
                      <MetricsTable label="After" data={item.after} />
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </article>
      ))}
    </div>
  );
}
